import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { locationApi } from '../config/api';

const LocationContext = createContext(null);

const ROUTING_KEY = 'zozo_plz_routing';
const ROUTING_TTL_MS = 30 * 24 * 3600 * 1000; // 30 Tage

function loadStoredRouting() {
  try {
    const raw = JSON.parse(localStorage.getItem(ROUTING_KEY) || 'null');
    if (!raw || !raw.filiale_id) return { routing: null, lastPlz: raw?.plz || '' };
    const fresh = raw.zuordnung_zeitpunkt && (Date.now() - new Date(raw.zuordnung_zeitpunkt).getTime() < ROUTING_TTL_MS);
    return fresh ? { routing: raw, lastPlz: raw.plz || '' } : { routing: null, lastPlz: raw.plz || '' };
  } catch {
    return { routing: null, lastPlz: '' };
  }
}

export function LocationProvider({ children }) {
  const [locations, setLocations] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [{ routing: initialRouting, lastPlz: initialLastPlz }] = useState(loadStoredRouting);
  const [plzRouting, setPlzRoutingState] = useState(initialRouting);
  const [lastPlz, setLastPlz] = useState(initialLastPlz);
  const [showPlzGate, setShowPlzGate] = useState(false);

  // Load locations on mount
  useEffect(() => {
    loadLocations();
  }, []);

  // Saved location wiederherstellen — NUR wenn PLZ-Routing existiert.
  // Ohne Routing bleibt der Standort bewusst leer („Standort wählen"),
  // damit nichts als vorausgewählt erscheint (Rellingen war nur techn. URL-Fallback).
  useEffect(() => {
    const savedSlug = plzRouting?.filiale_id;
    if (savedSlug && locations.length > 0) {
      const location = locations.find(l => l.slug === savedSlug);
      if (location) setSelectedLocation(location);
    }
  }, [locations, plzRouting]);

  const loadLocations = async () => {
    try {
      const response = await locationApi.getAll();
      setLocations(response.data.data || []);
    } catch (error) {
      console.error('Failed to load locations:', error);
    } finally {
      setIsLoading(false);
    }
    try {
      const res = await locationApi.getAnnouncements();
      setAnnouncements(res.data.data || []);
    } catch { /* keine Ankündigungen */ }
  };

  const selectLocation = (location) => {
    setSelectedLocation(location);
    localStorage.setItem('selectedLocation', location.slug);
    setShowLocationPicker(false);
  };

  // PLZ-Routing anwenden (Lieferung: {plz, filiale_id, modus:'lieferung'} | Abholung: {filiale_id, modus:'abholung'})
  const applyRouting = useCallback((data) => {
    const record = {
      plz: data.plz || '',
      filiale_id: data.filiale_id,
      modus: data.modus || 'lieferung',
      zuordnung_zeitpunkt: new Date().toISOString(),
    };
    localStorage.setItem(ROUTING_KEY, JSON.stringify(record));
    setPlzRoutingState(record);
    setLastPlz(record.plz);
    setShowPlzGate(false);
    localStorage.setItem('selectedLocation', record.filiale_id);
    const loc = locations.find(l => l.slug === record.filiale_id);
    if (loc) setSelectedLocation(loc);
    return record;
  }, [locations]);

  const clearRouting = useCallback(() => {
    localStorage.removeItem(ROUTING_KEY);
    setPlzRoutingState(null);
  }, []);

  // Harte Sperre: ohne gültiges Routing (Lieferung oder Abholung) kein Zugang zur Speisekarte
  const gateRequired = !plzRouting;

  const checkDeliveryZone = async (postalCode) => {
    if (!selectedLocation) return null;
    try {
      const response = await locationApi.checkDeliveryZone(postalCode, selectedLocation.slug);
      return response.data.data;
    } catch (error) {
      console.error('Delivery zone check failed:', error);
      return null;
    }
  };

  return (
    <LocationContext.Provider value={{
      locations,
      announcements,
      selectedLocation,
      selectLocation,
      isLoading,
      showLocationPicker,
      setShowLocationPicker,
      checkDeliveryZone,
      loadLocations,
      plzRouting,
      lastPlz,
      gateRequired,
      applyRouting,
      clearRouting,
      showPlzGate,
      setShowPlzGate,
    }}>
      {children}
    </LocationContext.Provider>
  );
}

export function useLocation() {
  const context = useContext(LocationContext);
  if (!context) {
    throw new Error('useLocation must be used within a LocationProvider');
  }
  return context;
}
